import { useEffect, useMemo, useState } from 'react';
import {
  Star, Search, MessageSquareQuote, ThumbsUp, BadgeCheck,
  ArrowUpDown, RotateCcw, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { useAssociateFeedbacks, useAssociateProfile, } from '@shared/hooks/use-associate';
import type { AssociateFeedback, AssociateFeedbacksQuery } from '@shared/types/api';
import { cn } from '@/lib/cn';

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest';
type StarFilter = 'all' | '5' | '4' | '3' | '2' | '1';

export function AssociateReviewsPage() {
  const { data: profile } = useAssociateProfile();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [starFilter, setStarFilter] = useState<StarFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce search input so backend API is called smoothly after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Construct backend API query parameters
  const query: AssociateFeedbacksQuery = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      rating: starFilter === 'all' ? undefined : parseInt(starFilter, 10),
      sortBy,
      page,
      pageSize,
    }),
    [debouncedSearch, starFilter, sortBy, page, pageSize],
  );

  // Fetch reviews directly via backend API with live query parameters
  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    refetch,
  } = useAssociateFeedbacks(query);

  const feedbacks = data?.items ?? [];
  const totalFiltered = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const isInitialLoading = isLoading && !data;

  // Global review statistics returned by the backend across all completed reviews
  const stats = useMemo(() => {
    if (data?.stats) {
      return data.stats;
    }
    return {
      total: profile?.ratingCount ?? 0,
      average: profile?.averageRating ?? 0,
      distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } as Record<number, number>,
      positiveRate: 0,
      fiveStarCount: 0,
    };
  }, [data?.stats, profile]);

  const handleFilterChange = (filter: StarFilter) => {
    setStarFilter(filter);
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8 space-y-6">
      {/* ── Breadcrumb & Page Title ───────────────────────────────── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mt-1 text-xl font-semibold text-gray-900">
            Client Reviews & Ratings
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Live client feedback and performance metrics queried directly from completed transactions.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900"
          >
            <RotateCcw className={cn('h-3.5 w-3.5', (isRefetching || isFetching) && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Overview & Statistics Section ─────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Card 1: Score & Star Distribution */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-xs lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            {/* Rating Block */}
            <div className="flex flex-col items-center justify-center rounded-lg bg-amber-50/60 border border-amber-100/80 p-3.5 min-w-[130px] text-center shrink-0">
              <span className="text-2xl font-bold text-gray-900">
                {stats.average > 0 ? stats.average.toFixed(1) : '–'}
              </span>
              <div className="mt-1">
                <StarRating value={stats.average} size="sm" />
              </div>
              <p className="mt-1.5 text-xs font-medium text-gray-500">
                Based on {stats.total} {stats.total === 1 ? 'review' : 'reviews'}
              </p>
            </div>

            {/* Star Distribution Bars (Clickable to trigger backend filter) */}
            <div className="flex-1 space-y-1.5">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = stats.distribution[star] || 0;
                const percentage =
                  stats.total > 0
                    ? Math.round((count / stats.total) * 100)
                    : 0;
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleFilterChange(star.toString() as StarFilter)}
                    className={cn(
                      'group flex w-full items-center gap-2.5 rounded-md px-2 py-0.5 text-xs transition-colors hover:bg-gray-50',
                      starFilter === star.toString() && 'bg-amber-50/70 font-semibold',
                    )}
                  >
                    <span className="flex items-center gap-1 w-8 text-gray-700">
                      <span>{star}</span>
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                    </span>
                    <div className="h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-500',
                          star >= 4
                            ? 'bg-amber-400'
                            : star === 3
                              ? 'bg-amber-300'
                              : 'bg-orange-300',
                        )}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-gray-500 group-hover:text-gray-900 text-[11px]">
                      {count} ({percentage}%)
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Card 2: Quick Highlights */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
          <div className="flex items-center gap-3.5 rounded-xl border border-gray-200 bg-white p-4 shadow-xs">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100">
              <ThumbsUp className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Positive Rating</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">
                {stats.positiveRate}%
              </p>
              <p className="text-[11px] text-gray-400">4 & 5 star ratings</p>
            </div>
          </div>

          <div className="flex items-center gap-3.5 rounded-xl border border-gray-200 bg-white p-4 shadow-xs">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 border border-amber-100">
              <BadgeCheck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">5-Star Reviews</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">
                {stats.fiveStarCount}
              </p>
              <p className="text-[11px] text-gray-400">Perfect score client reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Backend Filters & Search Toolbar ──────────────────────── */}
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-xs md:flex-row md:items-center md:justify-between">
        {/* Star Rating Pills (Calls backend query via API) */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => handleFilterChange('all')}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              starFilter === 'all'
                ? 'bg-brand-600 text-white shadow-xs'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            All ({stats.total})
          </button>
          {[5, 4, 3, 2, 1].map((s) => {
            const count = stats.distribution[s] || 0;
            return (
              <button
                key={s}
                type="button"
                onClick={() => handleFilterChange(s.toString() as StarFilter)}
                className={cn(
                  'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  starFilter === s.toString()
                    ? 'bg-brand-600 text-white shadow-xs'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
              >
                <span>{s}</span>
                <Star
                  className={cn(
                    'h-3 w-3',
                    starFilter === s.toString()
                      ? 'fill-white text-white'
                      : 'fill-amber-400 text-amber-400',
                  )}
                />
                <span className="opacity-75">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Search & Sort (Calls backend query via API) */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
          <div className="relative min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by client, service, code..."
              className="pl-8 pr-8 text-xs h-9 bg-gray-50 focus:bg-white transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setDebouncedSearch('');
                  setPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded cursor-pointer"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortOption);
                setPage(1);
              }}
              className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700 focus:border-brand-500 focus:bg-white focus:outline-none"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Rating</option>
              <option value="lowest">Lowest Rating</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Reviews Cards List ────────────────────────────────────── */}
      {isInitialLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse rounded-xl border border-gray-200 bg-white p-4 space-y-3 shadow-xs"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gray-200" />
                  <div className="space-y-1.5">
                    <div className="h-3.5 w-28 rounded bg-gray-200" />
                    <div className="h-3 w-20 rounded bg-gray-200" />
                  </div>
                </div>
                <div className="h-4 w-24 rounded bg-gray-200" />
              </div>
              <div className="h-10 w-full rounded bg-gray-100" />
            </div>
          ))}
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center shadow-xs">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-500 mb-2.5 border border-amber-100">
            <MessageSquareQuote className="h-6 w-6" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">
            {stats.total === 0
              ? 'No client reviews yet'
              : 'No matching reviews found'}
          </h3>
          <p className="mt-1 max-w-sm text-xs text-gray-500">
            {stats.total === 0
              ? 'When clients rate and submit feedback on completed transactions, they will appear here automatically.'
              : 'Try clearing your search query or changing the rating filters.'}
          </p>
          {stats.total > 0 && (searchQuery || starFilter !== 'all') && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setStarFilter('all');
                setPage(1);
              }}
              className="mt-3 px-3 py-1.5 text-xs"
            >
              Reset Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {feedbacks.map((fb) => (
            <ReviewCard key={fb.transactionId} feedback={fb} />
          ))}

          {/* Backend Pagination */}
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-xs bg-white">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalFiltered}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Review Card Component ─────────────────────────────────────────
function ReviewCard({ feedback }: { feedback: AssociateFeedback }) {
  const formattedDate = feedback.completedAt
    ? new Date(feedback.completedAt).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : null;

  const initials = feedback.clientName
    ? feedback.clientName
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
    : 'CL';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-xs transition-colors hover:border-gray-300">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {/* Client & Service info */}
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-700 text-xs font-bold text-white shadow-2xs">
            {initials}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-xs font-semibold text-gray-900">
                {feedback.clientName}
              </h4>
              {feedback.transactionCode && (
                <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono text-gray-600">
                  #{feedback.transactionCode}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-brand-700 font-medium">
              {feedback.serviceName}
            </p>
          </div>
        </div>

        {/* Rating & Date */}
        <div className="flex items-center justify-between sm:flex-col sm:items-end gap-1 border-t border-gray-100 pt-2 sm:border-0 sm:pt-0">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-xs font-bold text-amber-700 border border-amber-200/60">
              {feedback.rating.toFixed(1)}
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
            </span>
            <StarRating value={feedback.rating} size="sm" />
          </div>
          {formattedDate && (
            <span className="text-[11px] text-gray-400">{formattedDate}</span>
          )}
        </div>
      </div>

      {/* Description / Comment */}
      {feedback.feedbackDescription ? (
        <div className="mt-2.5 rounded-lg border border-gray-100 bg-gray-50/70 p-2.5">
          <div className="flex items-start gap-2">
            <MessageSquareQuote className="h-3.5 w-3.5 shrink-0 text-gray-400 mt-0.5" />
            <p className="text-xs text-gray-700 leading-relaxed italic">
              &ldquo;{feedback.feedbackDescription}&rdquo;
            </p>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-xs italic text-gray-400">
          No written feedback provided with this rating.
        </p>
      )}
    </div>
  );
}

// ── Star Rating Visualization ─────────────────────────────────────
function StarRating({
  value,
  size = 'md',
}: {
  value: number;
  size?: 'sm' | 'md' | 'lg';
}) {
  const filled = Math.round(value);
  const sizeClasses = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            sizeClasses[size],
            filled >= star
              ? 'fill-amber-400 text-amber-400'
              : 'fill-gray-100 text-gray-300',
          )}
        />
      ))}
    </div>
  );
}
